import GeofenceUserManagement from "@/services/api/GeofenceManagement";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface GeofenceManagementState {
    userGeofence: any;
    useGeofences: any[];
    userGeofencesLogs: any[];
    totalLogsRecords: number,
    geofenceArea: any;
    geofencingArea: any[] ;
    loading: {
        userGeofence: boolean;
        geofenceExport: boolean;
        userGeofences: boolean;
        userGeofencesLogs: boolean;
        userGeofenceArea: boolean;
        geofencingArea: boolean;
    };
    error: string | null;
}

const initialState: GeofenceManagementState = {
    userGeofence: null,
    useGeofences: [],
    userGeofencesLogs: [],
    totalLogsRecords: 0,
    geofenceArea: null,
    geofencingArea: [],
    loading: {
        userGeofence: false,
        geofenceExport: false,
        userGeofences: false,
        userGeofencesLogs: false,
        userGeofenceArea: false,
        geofencingArea: false,
    },
    error: null,
};

export const getUserGeofence = createAsyncThunk(
    "geofenceManagement/getUserGeofence",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await GeofenceUserManagement.getUserGeofence(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to feth user geo fence");
        }
    }
);

export const getGeofenceReport = createAsyncThunk(
    "geofenceManagement/getUserGeofenceReport",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await GeofenceUserManagement.getUserGeofenceReport(payload);
            return saveAsExcelFile(res, "USER_GEOFENCE_REPORT");
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to feth user geo fence report");
        }
    }
);
export const addUserGeofences = createAsyncThunk(
    "geofenceManagement/addUserGeofences",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await GeofenceUserManagement.addUserGeofences(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to add user geo fences");
        }
    }
);
export const removeUserGeofences = createAsyncThunk(
    "geofenceManagement/removeUserGeofences",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await GeofenceUserManagement.removeUserGeofences(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to remove user geo fences");
        }
    }
);
export const getUserGeofenceLogs = createAsyncThunk(
    "geofenceManagement/getUserGeofenceLogs",
    async (data: { from: number, to: number, searchText: string }, { rejectWithValue }) => {
        try {
            const res = await GeofenceUserManagement.getUserGeofenceLogs(data);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch user geo fence logs");
        }
    }

);
export const getUserGeofenceAreaById = createAsyncThunk(
    "geofenceManagement/getUserGeofenceAreaById",
    async (id: string | number, { rejectWithValue }) => {
        try {
            const res = await GeofenceUserManagement.getUserGeofenceAreaById(id);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch user geo fence area");
        }
    }
);

export const getGeofencingArea = createAsyncThunk(
    "geofenceManagement/getGeofencingArea",
    async (_, { rejectWithValue }) => {
        try {
            const res = await GeofenceUserManagement.getGeofencingArea();            
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || "Failed to fetch geofencing area");
        }   
    }
);

const geofenceManagementSlice = createSlice({
    name: "geofenceManagement",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getUserGeofence.pending, (state) => {
                state.error = null;
                state.loading.userGeofence = true;
            })
            .addCase(getUserGeofence.fulfilled, (state, action) => {
                state.error = null;
                state.userGeofence = action.payload || [];
                state.loading.userGeofence = false;
            })
            .addCase(getUserGeofence.rejected, (state, action) => {
                state.error = action.payload as string;
                state.userGeofence = [];
                state.loading.userGeofence = false;
            })
            .addCase(getGeofenceReport.pending, (state) => {
                state.error = null;
                state.loading.geofenceExport = true;
            })
            .addCase(getGeofenceReport.fulfilled, (state) => {
                state.error = null;
                state.loading.geofenceExport = false;
            })
            .addCase(getGeofenceReport.rejected, (state, action) => {
                state.error = action.payload as string;
                state.loading.geofenceExport = false;
            })
            .addCase(removeUserGeofences.pending, (state) => {
                state.error = null;
                state.loading.userGeofences = true;
            })
            .addCase(addUserGeofences.pending, (state) => {
                state.error = null;
                state.loading.userGeofences = true;
            })
            .addCase(addUserGeofences.fulfilled, (state) => {
                state.error = null;
                state.loading.userGeofences = false;
            })
            .addCase(addUserGeofences.rejected, (state, action) => {
                state.error = action.payload as string;
                state.loading.userGeofences = false;
            })
            .addCase(removeUserGeofences.fulfilled, (state) => {
                state.error = null;
                state.loading.userGeofences = false;
            })
            .addCase(removeUserGeofences.rejected, (state, action) => {
                state.error = action.payload as string;
                state.loading.userGeofences = false;
            })
            .addCase(getUserGeofenceLogs.pending, (state, action) => {
                state.error = null;
                state.loading.userGeofencesLogs = true;
                // reset list when starting a fresh query (from === 1)
                if (action.meta.arg.from === 1) {
                    state.userGeofencesLogs = [];
                    state.totalLogsRecords = 0;
                }
            })
            .addCase(getUserGeofenceLogs.fulfilled, (state, action) => {
                state.error = null;
                state.userGeofencesLogs = [...state.userGeofencesLogs, ...(action.payload?.Items || [])];
                state.totalLogsRecords = action.payload?.TotalRecords ?? state.totalLogsRecords;
                state.loading.userGeofencesLogs = false;
            })
            .addCase(getUserGeofenceLogs.rejected, (state, action) => {
                state.error = action.payload as string;
                state.loading.userGeofencesLogs = false;
            })
            .addCase(getUserGeofenceAreaById.pending, (state) => {
                state.error = null;
                state.loading.userGeofenceArea = true;
            })
            .addCase(getUserGeofenceAreaById.fulfilled, (state, action) => {
                state.error = null;
                state.geofenceArea = action.payload || null;
                state.loading.userGeofenceArea = false;
            })
            .addCase(getUserGeofenceAreaById.rejected, (state, action) => {
                state.error = action.payload as string;
                state.geofenceArea = null;
                state.loading.userGeofenceArea = false;
            }
            )
    },
});

export default geofenceManagementSlice.reducer;